import { Card, Paragraph, ScrollView, View, XStack ,Text} from '@ninjacart/nc-nk-ui';
import {
  termsPrivacy,
  useAppPersistenceStore,
  useCropPreferenceStore,
  useEventZStore,
  useProfileStore,
  useResumableFeedStore,
  UserProps,
  KissanEventType,
} from '@ninjacart/nc-nk-store';
import { useIsFocused, useRouter } from '@ninjacart/nc-nk-web-deps';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Linking, Platform, Pressable } from 'react-native';
import { Logout } from './logoutCard';
import { ProfileAnimalCard } from './profileAnimalCard';
import { ProfileCropsCard } from './profileCropsCard';
import ReferralCode from './referralCode';
import UserDetails from './userDetails';
import {
  useQueuedEventStore,
  addEventParam,
  INinjaEvent,
  resetPostHog,
  useEventStore,
} from '@ninjacart/nc-events-sdk-v2';
type ProfileParams = {
  userProfile: UserProps;
  languageChange: () => void;
  refCode: string;
  setProfileData: React.Dispatch<any>;
};
const MyProfileTab = ({ userProfile, languageChange, refCode, setProfileData }: ProfileParams) => {
  const hasUserLoggedIn = useProfileStore((state) => state.hasUserLoggedIn);
  const appLanguageLabel = useAppPersistenceStore((state) => state.appLanguageLabel);
  const [copied, setCopied] = useState(false);
  const setUserSkippedLogin = useProfileStore((state) => state.setUserSkippedLogin);
  const clearStorage = useProfileStore((state) => state.clearStorage);
  const setIsSpinnerVisible = useProfileStore((state) => state.setIsSpinnerVisible);
  const resetResumableFeedStore = useResumableFeedStore((state) => state.resetResumableFeedStore);
  const setOnLoginRedirect = useAppPersistenceStore((state) => state.setOnLoginRedirect);
  const { push } = useRouter();
  const setAdId = useAppPersistenceStore((state) => state.setAdId);
  const contactUsWhatsappNumber = useEventZStore((state) => state.contactUsWhatsappNumber);
  const enqueue = useRef(useQueuedEventStore((state) => state.enqueue)).current;
  const clearCropPreferenceStorage = useCropPreferenceStore(
    (state) => state.clearCropPreferenceStorage
  );
  const { t } = useTranslation();
  const isFocused = useIsFocused();
  useEffect(() => {
    let timer;
    if (copied) {
      timer = setTimeout(() => {
        setCopied(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [copied]);

  const handlePressButtonAsync = async (url, event) => {
    handleEventAction(event);

    if (Platform.OS === 'web') {
      window.open('/toc');
      return;
    }

    const canOpenChrome = await Linking.canOpenURL('googlechrome://');
    if (canOpenChrome) {
      await Linking.openURL(`googlechrome://navigate?url=${url}/toc`);
      return;
    }

    await Linking.openURL(`${url}/toc`);
  };
  const logOut = () => {
    if (Platform.OS === 'web') {
      const confirmed = window.confirm(`${t('page.wait')}\n\n${t('page.areYouLogout')}`);
      if (confirmed) {
        clearAllData();
      }
    } else {
      Alert.alert(
        `${t('page.wait')} !!!`,
        `${t('page.areYouLogout')}`,
        [
          {
            text: `${t('page.cancel')}`,
            style: 'cancel',
            onPress: () => { },
          },
          {
            text: `${t('page.ok')} `,
            onPress: () => {
              clearAllData();
            },
          },
        ],
        { cancelable: false }
      );
    }
  };
  const clearAllData = () => {
    handleLogoutevent();
    useEventStore.getState()?.clearEventUserDetails();
    resetPostHog();
    setUserSkippedLogin(false);
    setProfileData(undefined);
    clearStorage();
    clearCropPreferenceStorage();
    resetResumableFeedStore();
    setOnLoginRedirect(undefined);
    push('/login');
    setAdId('');
  };
  const handleLogoutevent = () => {
    const logoutEvent: INinjaEvent = {
      name: KissanEventType.USER_LOGOUT,
      screenDetails: { screen: 'profile detail page' },
      eventParams: [addEventParam('contactNumber', userProfile?.user?.primaryPhoneNumber)],
    };
    enqueue(logoutEvent);
  };
  const handleEventAction = (event) => {
    const eventToEnqueue: INinjaEvent = {
      name: event,
      screenDetails: { screen: 'edit profile page' },
    };
    enqueue(eventToEnqueue);
  };
  const handleWhatsAppContact = async () => {
    const phoneNumber = `${contactUsWhatsappNumber}`;
    const message = 'Hi';
    const whatsappURL = `whatsapp://send?phone=${phoneNumber}&text=${message}`;
    try {
      const supported = await Linking.canOpenURL(whatsappURL);
      if (supported) {
        await Linking.openURL(whatsappURL);
      } else {
        if (Platform.OS === 'ios') {
          Linking.openURL('https://apps.apple.com/us/app/whatsapp-messenger/id310633997');
        } else {
          Linking.openURL('https://play.google.com/store/apps/details?id=com.whatsapp');
        }
      }
    } catch (error) {
      console.error('Failed to open WhatsApp:', error);
    }
  };
  return (
    <ScrollView showsVerticalScrollIndicator={false}>
      {hasUserLoggedIn() && <UserDetails userProfile={userProfile} />}
      {isFocused && (
        <>
          <ProfileCropsCard />
          <ProfileAnimalCard />
        </>
      )}
      <XStack>
        <Card width={'100%'} marginVertical={2} backgroundColor="$cardbackground">
          <View padding={15}>
            <Paragraph size={12} fontWeight={'400'} paddingBottom={3} color={'$grey'}>
              {t('page.language')}
            </Paragraph>
            <XStack flexDirection="row" justifyContent="space-between">
              <Paragraph size={14} fontWeight={'500'} color={'$color'}>
                {appLanguageLabel}
              </Paragraph>
              <Pressable onPress={languageChange}>
                <Paragraph size={13} fontWeight={'500'} color={'$primary'} testID='kisan_tId_changeLanguage_click'>
                  {' '}
                  {t('page.change')}{' '}
                </Paragraph>
              </Pressable>
            </XStack>
          </View>
        </Card>
      </XStack>
      {refCode && hasUserLoggedIn() && <ReferralCode refCode={refCode} userProfile={userProfile} />}
      <XStack
        onPress={() =>
          handlePressButtonAsync(
            termsPrivacy()?.termsConditionsUrl,
            KissanEventType.PROFILE_T_AND_C_CLICK
          )
        }
        testID="kisan_tId_termsAndCondition_click"
      >
        <Card width={'100%'} marginVertical={2} backgroundColor="$cardbackground">
          <View padding={15}>
            <Text fontSize={14} color={'$color'} fontWeight={'500'} paddingBottom={3}>
              {t('page.termsAndConditions')}
            </Text>
          </View>
        </Card>
      </XStack>
      <XStack
        onPress={() =>
          handlePressButtonAsync(termsPrivacy()?.privacyPolicyUrl, KissanEventType.PROFILE_PP_CLICK)
        }
        testID="kisan_tId_privacyPolicy_click"
      >
        <Card width={'100%'} marginVertical={2} backgroundColor="$cardbackground">
          <View padding={15}>
            <Paragraph size={14} color={'$color'} fontWeight={'500'} paddingBottom={3}>
              {t('page.privacyPolicy')}
            </Paragraph>
          </View>
        </Card>
      </XStack>
      <XStack onPress={handleWhatsAppContact} testID="kisan_tId_contactUs_click">
        <Card width={'100%'} marginVertical={2} backgroundColor="$cardbackground">
          <View padding={15}>
            <Paragraph size={14} color={'$color'} fontWeight={'500'} paddingBottom={3}>
              {t('page.contactUs')}
            </Paragraph>
          </View>
        </Card>
      </XStack>
      {hasUserLoggedIn() && (
        <XStack marginBottom={Platform?.OS === 'web' ? 0 : 190}>
          <Logout logOut={logOut} />
        </XStack>
      )}
      {/* {Platform.OS === 'web' && <Footer sourcepage={'profile'} />} */}
    </ScrollView>
  );
};
export default MyProfileTab;